import type { Rule, RuleGroup } from "./ConversionRuleBuilder";

function formatValue(value: any): string {
  if (value == null) return "null";
  if (typeof value === "boolean") return value ? "true" : "false";
  if (typeof value === "number") return String(value);
  return JSON.stringify(String(value));
}

function escapeRegex(input: string): string {
  return input.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// A dimensioned rule appends `dimension.<key> == "<val>"` clauses. The backend
// merges the cell's dimension into rule_data under a top-level `dimension` key,
// so `dimension.period == "2026SP"` binds the rule to that one cell (MPN-1202).
// Keys are emitted in sorted order so the string is canonical/stable and
// round-trips (matches canonicalizeDimension in ConversionRuleBuilder).
function compileDimension(dimension?: Record<string, string>): string {
  if (!dimension) return "";
  return Object.keys(dimension)
    .sort()
    .filter((k) => dimension[k] != null && dimension[k] !== "")
    .map((k) => `dimension.${k} == ${formatValue(dimension[k])}`)
    .join(" and ");
}

function compileRule(rule: Rule): string {
  const base = compileComparison(rule);
  if (!base) return "";
  const dim = compileDimension(rule.dimension);
  return dim ? `${base} and ${dim}` : base;
}

function compileComparison(rule: Rule): string {
  // Field names carry Django `__` FK-traversal notation (e.g. `academic_period__code`).
  // rule_engine requires dot notation, so translate the field's `__` into `.`.
  const fieldPath = rule.field.replace(/__/g, ".");
  const path = rule.entity && rule.field ? `${rule.entity}.${fieldPath}` : "";
  if (!path || !rule.operator) return "";

  const v = rule.value;
  switch (rule.operator) {
    case "equals":
      return `${path} == ${formatValue(v)}`;
    case "not_equals":
      return `${path} != ${formatValue(v)}`;
    case "greater_than":
      return `${path} > ${formatValue(v)}`;
    case "less_than":
      return `${path} < ${formatValue(v)}`;
    case "greater_than_equal":
      return `${path} >= ${formatValue(v)}`;
    case "less_than_equal":
      return `${path} <= ${formatValue(v)}`;
    case "contains":
      return `${path} =~ ${JSON.stringify(escapeRegex(String(v ?? "")))}`;
    case "not_contains":
      return `${path} !~ ${JSON.stringify(escapeRegex(String(v ?? "")))}`;
    case "starts_with":
      return `${path} =~ ${JSON.stringify("^" + escapeRegex(String(v ?? "")))}`;
    case "ends_with":
      return `${path} =~ ${JSON.stringify(escapeRegex(String(v ?? "")) + "$")}`;
    case "in": {
      // A catalog value group by code; the backend expands it to the list (MPN-2328).
      if (rule.value_group) return `${path} in value_group.${rule.value_group}`;
      const arr = Array.isArray(v) ? v : [v];
      return `${path} in [${arr.map(formatValue).join(", ")}]`;
    }
    case "is_null":
      return `${path} == null`;
    case "is_not_null":
      return `${path} != null`;
    default:
      return `${path} == ${formatValue(v)}`;
  }
}

export function compileRuleGroup(group: RuleGroup): string {
  if (!group?.rules?.length) return "";
  const joiner = group.combinator === "AND" ? " and " : " or ";
  const parts = group.rules
    .map((r) => {
      if (!("combinator" in r)) return compileRule(r as Rule);
      // A group that compiles to nothing (empty, or all children incomplete)
      // must contribute nothing. Emitting `()` would survive the filter below
      // and produce `a and ()`, which rule_engine rejects at parse time.
      const inner = compileRuleGroup(r as RuleGroup);
      return inner ? `(${inner})` : "";
    })
    .filter((s) => s.length > 0);
  return parts.join(joiner);
}
